import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card, Button, Input } from '../../../components/ui';
import { fetchAPI } from '../../../services/apiClient';
import { Info, Save, Upload, Trash2 } from 'lucide-react';
import {
  TicketVisualConfig,
  TicketFontWeight,
  TicketFontSize,
  TicketConfig,
  TicketPayload,
  guardarVisualConfig,
  leerVisualConfig,
  invalidarConfigCache,
  renderTicketHTML,
} from '../../../services/ticketService';

const FONT_WEIGHT_OPTIONS: { value: TicketFontWeight; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Negrita' },
  { value: 'extra-bold', label: 'Extra negrita' },
];

const FONT_SIZE_OPTIONS: { value: TicketFontSize; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'grande', label: 'Grande' },
  { value: 'extra-grande', label: 'Extra grande' },
  { value: 'gigante', label: 'Gigante' },
  { value: 'extra-gigante', label: 'Extra gigante' },
];

export const VendedorConfigTicketPage: React.FC = () => {
  const [nombreTicket, setNombreTicket] = useState('');
  const [notasTicket, setNotasTicket] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [loading, setLoading] = useState(false);

  // Visual config (localStorage)
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [fontWeight, setFontWeight] = useState<TicketFontWeight>('normal');
  const [fontSize, setFontSize] = useState<TicketFontSize>('normal');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    cargarConfig();
    cargarVisual();
  }, []);

  const cargarConfig = async () => {
    try {
      const data = await fetchAPI<{ ticket_nombre: string | null; ticket_notas: string | null }>(
        '/operaciones/vendedor/ticket-config'
      );
      setNombreTicket(data.ticket_nombre || '');
      setNotasTicket(data.ticket_notas || '');
    } catch (error: any) {
      console.error('Error cargando configuración:', error);
    }
  };

  const cargarVisual = () => {
    const v = leerVisualConfig('vendedor');
    if (v.logoDataUrl) setLogoDataUrl(v.logoDataUrl);
    if (v.fontWeight) setFontWeight(v.fontWeight);
    if (v.fontSize) setFontSize(v.fontSize);
  };

  const guardarConfig = async () => {
    setLoading(true);
    setMensaje('');
    try {
      // 1. Guardar nombre/notas en API
      await fetchAPI('/operaciones/vendedor/ticket-config', {
        method: 'PUT',
        body: JSON.stringify({
          ticket_nombre: nombreTicket.trim() || null,
          ticket_notas: notasTicket.trim() || null,
        }),
      });

      // 2. Guardar visual en localStorage
      const visual: TicketVisualConfig = { logoDataUrl, fontWeight, fontSize };
      guardarVisualConfig('vendedor', visual);

      setMensaje('Configuración guardada correctamente');
      setTimeout(() => setMensaje(''), 3000);
    } catch (error: any) {
      setMensaje(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Logo upload → base64
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512_000) {
      setMensaje('Error: El logo no puede superar 500 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setLogoDataUrl(reader.result as string);
      invalidarConfigCache();
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoDataUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    invalidarConfigCache();
  };

  // ── Generar preview HTML real usando renderTicketHTML ──
  const previewHTML = useMemo(() => {
    const config: TicketConfig = {
      nombre: nombreTicket.trim() || 'TiemPos',
      notas: notasTicket.trim(),
      ancho: 80,
      logoUrl: null,
      logoDataUrl: logoDataUrl,
      fontWeight,
      fontSize,
    };
    const mockPayload: TicketPayload = {
      ticketCode: '#A8F2Q',
      ticketId: 'preview',
      sorteoNombre: 'Ejemplo',
      sorteoCierre: '03:00 PM',
      sorteoFecha: '',
      fecha: '28/02/2026 11:05 AM',
      vendedorNombre: '',
      localNombre: '',
      metodoPago: 'EFECTIVO',
      referencia: null,
      total: 2500,
      jugadas: [
        { num1: '01', monto: 1000 },
        { num1: '05', monto: 1000 },
        { num1: '12', monto: 1000 },
        { num1: '23', monto: 1000 },
        { num1: '40', monto: 500 },
        { num1: '55', monto: 500 },
      ],
      config,
      esReimpresion: false,
      pagaVecesAplicado: null,
      clienteNombre: 'Cliente',
    };
    return renderTicketHTML(mockPayload);
  }, [nombreTicket, notasTicket, logoDataUrl, fontWeight, fontSize]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Configuración de Ticket</h1>
        <p className="text-gray-500 mt-2">Personaliza cómo se ve tu nombre en los tickets</p>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <Info className="text-blue-600 flex-shrink-0" size={20} />
        <div>
          <p className="text-blue-900 font-medium">Cómo funciona:</p>
          <p className="text-blue-800 text-sm mt-1">
            Esta configuración aplica <strong>solo a tus tickets</strong>. Si dejas los campos vacíos, se usará la configuración del local.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formulario */}
        <Card>
          <div className="space-y-4">
            {/* Logo */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Logo del ticket (opcional)
              </label>
              <p className="text-xs text-gray-500 mb-2">Imagen PNG o JPG, máximo 500 KB. Se guarda solo en este navegador.</p>
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2"
                >
                  <Upload size={16} />
                  {logoDataUrl ? 'Cambiar logo' : 'Subir logo'}
                </Button>
                {logoDataUrl && (
                  <Button variant="secondary" onClick={handleRemoveLogo} className="flex items-center gap-2 text-red-600">
                    <Trash2 size={16} />
                    Quitar
                  </Button>
                )}
              </div>
              {logoDataUrl && (
                <div className="mt-3 w-full max-w-sm rounded border bg-gray-50 p-3">
                  <div className="mx-auto flex h-40 w-28 items-center justify-center sm:h-56 sm:w-36 md:h-64 md:w-40 lg:h-72 lg:w-44">
                    <img
                      src={logoDataUrl}
                      alt="Logo preview"
                      className="h-full w-full object-contain"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Nombre a imprimir en el ticket
              </label>
              <p className="text-xs text-gray-500 mb-2">Máximo 32 caracteres (se ajusta automáticamente)</p>
              <Input
                type="text"
                value={nombreTicket}
                onChange={(e) => setNombreTicket(e.target.value.slice(0, 32))}
                placeholder="ej: Juan Pérez - Vendedor"
                maxLength={32}
              />
              <p className="text-xs text-gray-400 mt-1">{nombreTicket.length}/32</p>
            </div>

            {/* Notas */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Notas personalizadas (opcional)
              </label>
              <p className="text-xs text-gray-500 mb-2">Aparecerá al final del ticket. Máximo 500 caracteres</p>
              <textarea
                value={notasTicket}
                onChange={(e) => setNotasTicket(e.target.value.slice(0, 500))}
                placeholder="ej: Gracias por su compra. Vuelva pronto!"
                maxLength={500}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">{notasTicket.length}/500</p>
            </div>

            {/* Font weight */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Grosor de fuente</label>
              <div className="flex gap-2">
                {FONT_WEIGHT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setFontWeight(opt.value)}
                    className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                      fontWeight === opt.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Font size */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">Tamaño de fuente</label>
              <div className="flex gap-2">
                {FONT_SIZE_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setFontSize(opt.value)}
                    className={`px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                      fontSize === opt.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600">
                <strong>Sin configuración:</strong> Se usará la configuración de tu local
              </p>
            </div>

            {mensaje && (
              <div className={`p-3 rounded-lg text-sm ${
                mensaje.includes('Error')
                  ? 'bg-red-50 text-red-700'
                  : 'bg-green-50 text-green-700'
              }`}>
                {mensaje}
              </div>
            )}

            <Button
              onClick={guardarConfig}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2"
            >
              <Save size={18} />
              {loading ? 'Guardando...' : 'Guardar configuración'}
            </Button>
          </div>
        </Card>

        {/* Preview */}
        <Card>
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Vista previa (80mm)</h3>
            <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 overflow-hidden flex justify-center">
              <iframe
                ref={useCallback((iframe: HTMLIFrameElement | null) => {
                  if (!iframe) return;
                  const doc = iframe.contentDocument;
                  if (!doc) return;
                  doc.open();
                  doc.write(previewHTML);
                  doc.close();
                }, [previewHTML])}
                title="Vista previa del ticket"
                style={{
                  width: '512px',
                  maxWidth: '100%',
                  height: '520px',
                  border: 'none',
                  background: '#fff',
                }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center">
              Ancho máximo: 32 caracteres | Impresora térmica 80mm
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default VendedorConfigTicketPage;
